import React, { useEffect, useState, useCallback } from 'react';
import { merchantApi } from '../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faLock, faShieldAlt, faKey,
  faExclamationTriangle, faQrcode,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UiPageHeader, UiButton } from '../components/ui';

const TwoFAPage: React.FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ enabled: false });
  const [setupData, setSetupData] = useState<any>(null);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    try {
      const response = await merchantApi.get('/2fa/status');
      setStatus(response.data.data);
    } catch (error) {
      toast.error(t('common.cannotLoadData'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSetup = async () => {
    try {
      const response = await merchantApi.post('/2fa/setup');
      setSetupData(response.data.data);
      toast.success(t('twofa.setupSuccess'));
    } catch (error: any) {
      toast.error(t('common.error'));
    }
  };

  const handleEnable = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await merchantApi.post('/2fa/enable', { otp });
      toast.success(t('twofa.enableSuccess'));
      setSetupData(null);
      setOtp('');
      loadStatus();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('auth.otpInvalid'));
    }
  };

  const handleDisable = async () => {
    const otpCode = window.prompt(t('twofa.disablePrompt'));
    if (!otpCode) return;

    try {
      await merchantApi.post('/2fa/disable', { otp: otpCode });
      toast.success(t('twofa.disableSuccess'));
      loadStatus();
    } catch (error: any) {
      toast.error(t('auth.otpInvalid'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full">
      <UiPageHeader
        icon={faShieldAlt}
        title="Two-Factor Authentication (2FA)"
        subtitle={t('twofa.desc')}
      />

      {/* Not Enabled & No Setup */}
      {!status.enabled && !setupData && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-8 text-center mx-auto">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
            <FontAwesomeIcon icon={faLock} className="text-4xl" style={{ color: 'var(--theme-primary)' }} />
          </div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100 mb-3">{t('twofa.enhanceSecurity')}</h2>
          <p className="text-gray-500 dark:text-slate-400 mb-6">{t('twofa.twofaHelps')}</p>

          <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-5 mb-6 text-left">
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-gray-700 dark:text-slate-200">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                {t('twofa.protectLogin')}
              </li>
              <li className="flex items-center gap-3 text-gray-700 dark:text-slate-200">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                {t('twofa.protectSettlement')}
              </li>
              <li className="flex items-center gap-3 text-gray-700 dark:text-slate-200">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                {t('twofa.useGoogleAuth')}
              </li>
            </ul>
          </div>

          <div className="flex justify-center">
            <UiButton icon={faShieldAlt} onClick={handleSetup}>
              {t('twofa.startSetup')}
            </UiButton>
          </div>
        </div>
      )}

      {/* Setup Mode */}
      {setupData && (
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Step 1: QR Code */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-10 h-10 rounded-full text-white flex items-center justify-center font-bold" style={{ background: 'var(--theme-primary)' }}>1</div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faQrcode} style={{ color: 'var(--theme-primary)' }} />
                {t('twofa.scanQrCode')}
              </h2>
            </div>
            <div className="text-center">
              <img
                src={setupData.qrCode}
                alt="2FA QR Code"
                className="w-48 h-48 mx-auto mb-4 rounded-xl border-4 border-gray-200 dark:border-slate-700 bg-white"
              />
              <p className="text-gray-600 dark:text-slate-300">
                {t('twofa.scanQrDesc')}
              </p>
            </div>
          </div>

          {/* Step 2: Backup Codes */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-10 h-10 rounded-full text-white flex items-center justify-center font-bold" style={{ background: 'var(--theme-primary)' }}>2</div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faKey} className="text-amber-500" />
                Backup Codes
              </h2>
            </div>

            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-500 mt-0.5" />
                <div>
                  <strong className="text-amber-800 dark:text-amber-300">{t('twofa.backupCodesImportant')}</strong>
                  <p className="text-amber-700 dark:text-amber-400 text-sm">{t('twofa.backupCodesDesc')}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {setupData.backupCodes.map((code: string, i: number) => (
                <div key={i} className="bg-gray-50 dark:bg-slate-700/50 rounded-lg px-4 py-2 flex items-center gap-2">
                  <span className="text-gray-400 dark:text-slate-500 text-sm font-medium">{i + 1}.</span>
                  <code className="font-mono text-gray-800 dark:text-slate-100">{code}</code>
                </div>
              ))}
            </div>
          </div>

          {/* Step 3: Verify */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-10 h-10 rounded-full text-white flex items-center justify-center font-bold" style={{ background: 'var(--theme-primary)' }}>3</div>
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500" />
                {t('twofa.confirmSetup')}
              </h2>
            </div>

            <form onSubmit={handleEnable} className="text-center">
              <p className="text-gray-600 dark:text-slate-300 mb-4">{t('twofa.enterCodeToConfirm')}</p>
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                maxLength={6}
                required
                className="w-48 px-4 py-4 border-2 border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-center text-3xl font-bold tracking-[0.5rem] focus:ring-2 focus:ring-primary/20 focus:border-primary mb-4"
              />
              <UiButton type="submit" icon={faCheckCircle} className="w-full justify-center">
                {t('twofa.enable2fa')}
              </UiButton>
            </form>
          </div>
        </div>
      )}

      {/* Enabled */}
      {status.enabled && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-8 text-center max-w-xl mx-auto">
          <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-6">
            <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-slate-100 mb-3">{t('twofa.enabled')}</h2>
          <p className="text-gray-500 dark:text-slate-400 mb-6">{t('twofa.enabledDesc')}</p>

          <div className="flex justify-center">
            <UiButton variant="danger" icon={faLock} onClick={handleDisable}>
              {t('twofa.disable2fa')}
            </UiButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default TwoFAPage;
